import { afterNextRender, AfterViewInit, Directive, ElementRef, inject, Injector, Input, OnDestroy, Renderer2, } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';

@Directive({
    selector: '[finderMultipleItemsPerRow]',
    standalone: true
})
export class MultipleItemsPerRowDirective implements AfterViewInit, OnDestroy {
    private processing = false;
    /* istanbul ignore next */
    private observer;
    private destroy$ = new Subject<void>();
    private injector = inject(Injector);

    @Input() finderMultipleItemsPerRow: number;
    @Input() finderHasTabletColumns = false;

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private viewportService: ViewportDetectionService
    ) {
        // @SSR: Make sure this runs in the browser
        afterNextRender(() => {
            this.observer = new MutationObserver(() => this.updateLayout());
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Check before calling the browser only object
        this.observer?.disconnect();
    }

    ngAfterViewInit(): void {
        // Check if layout should be updated
        if (
            !this.finderMultipleItemsPerRow ||
            this.finderMultipleItemsPerRow < 2 ||
            this.finderMultipleItemsPerRow > 4
        ) {
            return;
        }

        this.updateLayout();

        this.viewportService
            .onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.updateLayout());

        // @SSR: Make sure this runs in the browser
        afterNextRender(() => {
            /**
             * We need to check when other elements are added to the DOM
             * and re apply changes. i.e. package cards components
             */
            this.observer.observe(this.elementRef.nativeElement, {
                attributes: false,
                childList: true,
                subtree: true,
            });
        }, { injector: this.injector });
    }

    // Add classes to set max items and whether or not there is a tablet layout
    private updateLayout(): void {
        // We add a flag to avoid an endless loop due to DOM changes
        /* istanbul ignore if */
        if (this.processing) {
            return;
        }

        this.processing = true;
        const el: HTMLElement = this.elementRef.nativeElement;
        const elementChildren: Element[] = Array.from(el?.children);
        let maxItemsPerRow = this.finderMultipleItemsPerRow;

        if (!elementChildren?.length) {
            this.processing = false;

            return;
        }

        const viewportChecker = this.finderHasTabletColumns ?
            this.viewportService.isMobile() :
            !this.viewportService.isDesktop();

        if (viewportChecker) {
            this.renderer.removeClass(el, 'row-layout');
            this.renderer.removeClass(el, `row-max-${this.finderMultipleItemsPerRow}`);
            this.renderer.removeClass(el, `has-tablet-columns`);
            elementChildren.forEach((child: HTMLElement) => {
                this.renderer.removeClass(child, 'row-item');
            });
            this.processing = false;

            return;
        }

        // We handle tablet version reducing by one
        if (
            this.finderHasTabletColumns &&
            this.viewportService.isTablet() &&
            maxItemsPerRow > 2
        ) {
            maxItemsPerRow--;
        }

        // Add class to parent element
        this.renderer.addClass(el, 'row-layout');
        this.renderer.addClass(el, `row-max-${this.finderMultipleItemsPerRow}`);
        if (this.finderHasTabletColumns) {
            this.renderer.addClass(el, `has-tablet-columns`);
        }

        // Apply the class for each inner child
        elementChildren.forEach((child: HTMLElement) => {
            this.renderer.addClass(child, 'row-item');
        });

        this.processing = false;
    }
}
